import { EventDate, EventDateData, Venue, APIResponse, VenueSection, SeatStatus, DepositType, DeliveryMethod, Coupon, Location, LoginSource, PaymentPlan, EventPaymentPlan, ShortUser, SystemAccessType, SystemAccess, PaymentMethod, CatalogEvent, SectionSeatmap } from '@arema/components/Classes';
import { EventData, Scanner, OrderRefund, SectionHold, Zone, PdvDepositSource, PdvStats, PaymentTerminal, PaymentTerminalLicense, AccountOwnerType, BankAccount, ConciliaDeposit, ParsedOrder, ListPayout, ReportResponse, BankBin, ExternalSearchOrder, FoundExternalDeposit, ReportsAccessType, UserReportsEvent, UserReportsPromoter, BusinessBank, ListPayoutGroup, InvalidParsedOrder, PDV, PDVDepositType, AccountingAmexPayment, PayfrontAccountingPayment, AssetFile, AssetRelations, AssetType, AcquirerService, Promoter, PapeletaPdvData, CancelReason, RefundType } from './AdminClasses';
import { fromUnixLocal, getNowLocal, partition, removeAccents } from '@arema/components/Util';
import { PAGE_AUTH } from './AdminConfig';
import { EventPaymentSources, EventBusinessSource, PapeletaData, PDVDeposit, EventOrderConfig, EventOrder, EventCodeList, EventCodeCreate, EventCodeGroup } from '@arema/types/internal';
import axios, { ResponseType } from 'axios';
import moment from 'moment';
import { SystemConfig } from '@arema/types/admin';
const ROOT_URL = process.env.REACT_APP_API_URL
const DEBUG = process.env.REACT_APP_DEBUG=='1';

export interface TokenData{ 
	access: string, 
	expires: number,
}

var CACHE : { [x: string]: APIResponse<any> } = {};

var getToken = () : TokenData=>{
	var jsonstr = localStorage.getItem('admin_token');
	if(!jsonstr) return null;
	try{
		var data = JSON.parse(jsonstr) as TokenData;
		if(!data) return null;
		if(!data.access) return null;
		var now = moment().unix();
		if(data.expires<=(now+30)) return null;
		return data;
	}catch(e){
		return null;
	}
}

var saveToken = (token_data: TokenData)=>{
	localStorage.setItem('admin_token', JSON.stringify(token_data));
	window.dispatchEvent(new Event('user_token'));
}

var logout = ()=>{
	localStorage.removeItem('admin_token');
	localStorage.removeItem('user_data');
	window.dispatchEvent(new Event('user_data'));
	window.dispatchEvent(new Event('user_logout'));
}

interface PostOptions{
	login?: boolean,
	response_type?: ResponseType,
	access_token?: string,
}

async function post<T=any>(endpoint: string, data?: any, opts: PostOptions={}) : Promise<APIResponse<T>>{
	var options = {
		headers: {
			'show-code': DEBUG ? 1 : 0,
			'token': '',
			'Content-Type': 'application/json'
		}
	}
	if(opts?.login!==false){
		if(opts?.access_token){
			options.headers.token = `${opts.access_token}`;
		}else{
			var u = getToken();
			if(u===null){
				logout();
				return { error: true, code: 'PV-INVCRD', message: 'Las credenciales de accceso no son válidas (LCL-TKNCH-L).' }
			}
			options.headers.token = `${u.access}`;
		}
	}
	if(data instanceof FormData){
		options.headers['Content-Type'] = 'multipart/form-data';
	}

	try{
		var ENDPOINT = `${ROOT_URL}/${endpoint}`;
		if(DEBUG) console.log("POST "+ENDPOINT);
		var res = await axios.post(ENDPOINT, data, {
			...options,
			responseType: opts?.response_type || 'json'
		});
		if(DEBUG) console.log(` => Response: ${(res.headers['content-length']/1024).toFixed(2)} KB`);
		if(res.data && res.data.error && (res.data.code=='P-TKNE' || res.data.code=='P-TKNV')){
			logout();
		}
		return res.data as APIResponse<T>;
	}catch(err){
		if(DEBUG) console.error(err);
		if((err as any)?.code==="ERR_NETWORK"){
			return {
				error: true, 
				message: 'No se pudo establecer una conexión con el servidor. (API-NONTWK)',
				code: 'API-NONTWK',
			}
		}
		return {
			error: true, 
			message: 'Hubo un error inesperado (API-LCL-1)',
			code: 'API-LCL-1',
		}
	}
};

var cachePost = async <T=any>(endpoint: string, key: string, data?: any) : Promise<APIResponse<T>>=>{
	if(CACHE[key]) return CACHE[key] as APIResponse<T>;
	var res = await post(endpoint, data);
	if(!res.error) CACHE[key] = res;
	return res;
}

var getAuthUrl = ()=>{
	return `${PAGE_AUTH}?s=${LoginSource.ADMIN}&continue=${encodeURIComponent(`${window.location.origin}/auth`)}&redirect=${encodeURIComponent(window.location.href)}`
}

export interface UserLogin{
	user: {
		user_id: number,
		name?: string,
		email: string,
		first_name: string,
		last_name: string,
		superadmin: boolean,
		change_password: boolean,
	},
	access: number[]
}

var USER_LOGIN_CACHE : UserLogin = null;

var getLogin = async (force: boolean=false, token: string=null, dispatch_event: boolean=true)=>{
	if(!force && USER_LOGIN_CACHE) return { error: false, data: USER_LOGIN_CACHE, message: '' }
	var res = await post<UserLogin>('auth/me', {}, {
		access_token: token
	});
	if(res.error) return res;
	USER_LOGIN_CACHE = res.data;
	localStorage.setItem('user_data', JSON.stringify(res.data));
	if(dispatch_event) window.dispatchEvent(new Event('user_data'));
	return res;
}

var onLoginToken = async (token: string, expiration: number, dispatch_event: boolean=true)=>{
	var res = await getLogin(true, token, false);
	if(!res.error){
		saveToken({ access: token, expires: expiration });
		if(dispatch_event) window.dispatchEvent(new Event('user_data'));
	}
	return res;
}

// var changePassword = async (old_password: string, new_password: string)=>{
// 	var res = await post('users/me/password', { old_password, new_password });
// 	if(!res.error) logout();
// 	return res;
// }

var getPopularEvents = (offset: number=0, count: number=50)=>{
	return cachePost<CatalogEvent[]>('events/popular', 'popular_events', { offset, count });
}

var listEvents = (offset: number=0, count: number=50)=>{
	return post<(CatalogEvent & { date_created: number, dates: number })[]>('events/list', { offset, count });
}

var searchEvent = (query: string, offset: number=0, count: number=50)=>{
	return post<CatalogEvent[]>('events/search', { query, offset, count });
}

export type EventQL = 'promoter' | 'categories' | 'types' | 'cities' | 'payment_methods' | 'payment_methods_pdv' | 'payment_methods_all' | 'delivery_methods' | 'locations' | 'event_date_status' | 'event_group_category';
var getEventsQL = async (tables: EventQL[]) : Promise<APIResponse<{[x in EventQL]: any[]}>>=>{
	var missing: EventQL[] = [], ql_data: any = {};
	for(var i of tables){
		if(CACHE[`events/ql/${i}`]){
			ql_data[i] = CACHE[`events/ql/${i}`].data;
		}else{
			missing.push(i);
		}
	}
	if(missing.length!=0){
		var res = await post('events/ql', missing);
		if(!res.error){
			for(var k in (res.data as EventQL[])){
				ql_data[k] = res.data[k];
				CACHE[`events/ql/${k}`] = { error: false, data: res.data[k], code: null, message: null };
			}
		}else{
			return res;
		}
	}

	return { error: false, data: ql_data } as APIResponse<{[x in EventQL]: any[]}>
}

var clearEventsQL = (tables: EventQL[])=>{
	for(var i of tables){
		delete CACHE[`events/ql/${i}`];
	}
}

var createEvent = (event_name: string, promoter_id: number, event_type: number, category_id: number, options?: EventOptions)=>{
	return post<{ event_id: number }>('events/create', {
		event_name, promoter_id, event_type, category_id,
		options,
	});
}

var getEvent = (event_id: number, meta: boolean=false)=>{
	return post<EventData>('events/get', { event_id, meta: meta ? 1 : 0 });
}

var getEventStatus = (event_id: number)=>{
	return post<string[]>('events/status', { event_id });
}

var setEventPoster = (event_id: number, file: File)=>{
	var data = new FormData();
	data.append('event_id', event_id.toString());
	data.append('poster', file);
	return post<boolean>('events/poster', data);
}

var saveSinopsis = (event_id: number, sinopsis: string)=>{
	return post('events/sinopsis', { event_id, sinopsis });
}

var editEvent = (event_id: number, event_name: string, promoter_id: number, event_type: number, category_id: number, producer_name: string)=>{
	return post<boolean>('events/edit', { event_id, event_name, promoter_id, event_type, category_id, producer_name });
}

var getEventDates = (event_id: number, offset: number=0, count: number=99999)=>{
	return post<EventDate[]>('events/date/list', { event_id, offset, count });
}

var getCityVenues = (city: string)=>{
	return post<Venue[]>('venues/list/cities', { city });
}

var getAllVenues = (query: string, offset: number=0, count: number=30) => {
	return post<Venue[]>('venues/list', { offset, count, query });
}

var createVenue = (venue_name: string, zone_id: string, location: Location) => {
	return post<{ venue_id: number }>('venues/create', { venue_name, zone_id, location });
}

var editVenue = (venue_id: number, venue_name: string, zone_id: number, location?: Location)=>{
	return post('venues/edit', { venue_id, venue_name, zone_id, location });
}

var getVenue = (venue_id: number) => {
	return post<Venue>('venues/get', { venue_id });
}

var getAllVenueSections = (venue_id: number, query: string, offset=0, count=30) => {
	return post<VenueSection[]>(`venues/sections/search`, { venue_id, offset, count, query });
}

var deleteVenue = (venue_id: number)=>{
	return post<boolean>('venues/delete', { venue_id })
}

interface SectionAddData{
	section_name: string,
	description: string,
	infinite: boolean,
	numbered: boolean,
	ticket_count: number,
}

var createVenueSection = (venue_id: number, section_data: SectionAddData) => {
	return post<{ section_id: number }>(`venues/section/create`, { venue_id, ...section_data });
}

var editVenueSection = (section_id: number, section_data: SectionAddData)=>{
	return post('venues/section/edit', { section_id, ...section_data });
}

var cloneVenueSection = (section_id: number, section_name: string, description: string)=>{
	return post<{ section_id: number }>('venues/section/clone', { section_id, section_name, description });
}

interface SeatmapEditData{
	seats_width: number,
	seats_height: number,
	stage_x: number,
	stage_y: number,
	stage_width: number,
	stage_height: number,
}
interface SeatEditData{
	seat_id?: number,
	seat_row: string,
	seat_number: string,
	seat_section: string,
	seat_x: number,
	seat_y: number,
	seat_status: SeatStatus,
	status_comment: string,
	deleted: boolean,
}

var saveSeats = (section_id: number, date_id: number, seatmap: SeatmapEditData, seats: SeatEditData[]) => {
	return post<{
		new_seats: { seat_id: number, seat_x: number, seat_y: number }[]
	}>('venues/section/seats/edit', {
		section_id,
		date_id,
		seatmap,
		seats
	});
}

var getVenueSection = (venue_id: number, section_id: number, date_id: number, seats: boolean=false) => {
	return post<VenueSection>(`venues/section/get`, { section_id, venue_id, date_id, seats });
}

var createDate = (event_id: number, date: number, venue_id: number)=>{
	return post<{ date_id: number }>('events/date/create', { event_id, date, venue_id });
}

type EVENT_DATE_OPTS = 'sections' | 'prices' | 'date_check' | 'venue' | 'payment_methods' | 'delivery';
var getDate = (date_id: number, options?: EVENT_DATE_OPTS[], exclude?: EVENT_DATE_OPTS[])=>{
	var payload: any = { date_id };
	if(options){
		for(var i of options){
			payload[i] = 1;
		}
	}
	if(exclude){
		for(var i of exclude){
			payload[i] = 0;
		}
	}
	return post<EventDateData>('events/date/get', payload);
}

var getDateDelivery = (date_id: number)=>{
	return post<EventDateData["delivery"]>('events/date/get/delivery', { date_id });
}

interface EditDateData{
	date?: number,
	published?: boolean,
	active_pdv?: boolean,
	active_web?: boolean,
	date_web_start?: number,
	date_web_end?: number,
	date_pdv_start?: number,
	date_pdv_end?: number,
	card_commission?: number,
	scanner_start?: number,
	scanner_end?: number,
	status_id?: number,
}

var editDate = (date_id: number, data: EditDateData)=>{
	return post('events/date/edit', { date_id, ...data });
}

var getDateSections = (date_id: number, prices: boolean=true, seats_url: boolean=false)=>{
	return post('events/date/section/list', { date_id, prices: prices ? 1 : 0, seats: seats_url });
}

var getDateSectionStatus = (date_id: number)=>{
	return post('events/date/section/list/status', { date_id });
}

var setDateSectionTicketCount = (date_id: number, section_id: number, ticket_count: number)=>{
	return post('events/date/section/tickets', { date_id, section_id, ticket_count });
}

var updateDateSectionsOrder = (date_id: number, sections : { section_id: number, sort_order: number }[])=>{
	return post('events/date/section/order', { date_id, sections });
}

interface SectionPrice{
	price_name: string,
	cost: number,
	commission: number,
	min_tickets: number,
	max_tickets: number,
	multiple: number,
	date_start: number,
	date_end: number,
}

var addDateSection = (date_id: number, section_id: number, date_start: number, date_end: number)=>{
	return post('events/date/section/add', {
		date_id, section_id, date_start, date_end
	});
}

var removeDateSection = (date_id: number, section_id: number)=>{
	return post('events/date/section/remove', {
		date_id, section_id
	});
}

var addDatePaymentMethod = (date_id: number, method_id: number, date_start: number, date_end: number, force_show: boolean, commission_amount: number, commission_percent: number)=>{
	return post('events/date/payment/add', { date_id, method_id, date_start, date_end, force_show, commission_amount, commission_percent });
}

var removePaymentMethod = (date_id: number, method_id: number)=>{
	return post('events/date/payment/remove', { date_id, method_id });
}

var getUserList = (query: string, offset=0, count=30, systems?: SystemAccessType)=>{
	return post('users/list', { query, offset, count, systems })
}

interface UserCreateData{
	first_name: string,
	last_name: string,
	email: string,
	password?: string,
}
var createUser = (data: UserCreateData, systems: number[])=>{
	return post<{ user_id: number }>('users/create', { ...data, systems });
}

var addDateDelivery = (date_id: number, delivery_id: number, delivery_cost: number, date_start: number, date_end: number)=>{
	return post('events/date/delivery/add', {
		date_id, delivery_id, delivery_cost, date_start, date_end
	});
}

var removeDateDelivery = (date_id: number, delivery_id: number)=>{
	return post('events/date/delivery/remove', {
		date_id, delivery_id
	});
}

var editPrice = (price_id: number, data: SectionPrice)=>{
	var edit_data = { ...data };
	try{
		delete (edit_data as any).section_id;
	}catch(e){}
	return post('events/date/price/edit', { price_id, ...edit_data });
}

var updatePriceOrder = (prices : { price_id: number, sort_order: number }[])=>{
	return post('events/date/price/order', { prices });
}

var addPrice = (date_id: number, section_id: number, data: SectionPrice)=>{
	return post('events/date/price/add', { date_id, section_id, ...data });
}

var getDateSeatmap = (date_id: number, section_id: number)=>{
	return post<SectionSeatmap>('events/date/map', { date_id, section_id });
}

var setDateSeatStatus = (date_id: number, status: number, seats: number[], status_comment: string)=>{
	return post('events/date/map/status', { date_id, status, seats, status_comment });
}

var setDateSeatCost = (date_id: number, cost: number, commission: number, seats: number[])=>{
	return post('events/date/map/cost', { date_id, cost, seats, commission });
}

interface DuplicateChange{
	days: number,
	hours: number,
	minutes: number,
}
var duplicateDate = (date_id: number, origin: number, start_date: number, amount: number, start_now: boolean, change: DuplicateChange)=>{
	return post('events/date/multiply', {
		date_id, start_date, amount, start_now, change, origin
	});
}

interface EventOptions{
	subdomain?: string,
	access_code?: string,
	published?: boolean,
	meta_pixel?: string,
	google_tag?: string,
	tiktok_pixel?: string,
	user_assigned?: number,
	secret?: boolean,
	producer_name?: string,
	commission_return?: number,
	dynamic_code?: boolean,
	date_start?: number,
	date_publish?: number,
}
var setEventOptions = (event_id: number, options: EventOptions)=>{
	return post('events/options', { event_id, ...options });
}

var setEventSecret = (event_id: number, secret: boolean)=>{
	return post('events/options/secret', { event_id, secret });
}

var setEventUserAssigned = (event_id: number, user_id: number)=>{
	return post('events/assigned', { event_id, user_id });
}

var setEventBank  = (event_id: number, bank_id: number)=>{
	return post('events/bank', { event_id, bank_id });
}

interface GetUserParams{
	access?: boolean,
	all_access?: boolean,
	systems?: boolean,
}
var getUser = (user_id: number, params: GetUserParams)=>{
	return post('users/get', { 
		user_id, 
		access: params?.access ? 1 : 0,
		all_access: params?.all_access ? 1 : 0,
		systems: params?.systems ? 1 : 0,
	});
}

interface EditUserData{
	email?: string,
	first_name?: string,
	last_name?: string,
	active?: boolean,
	change_password?: boolean,
}
var editUser = (user_id: number, data: EditUserData)=>{
	return post('users/edit', { user_id, data });
}

var saveAccess = (user_id: number, access: number[])=>{
	return post('users/access', { user_id, access });
}

var getRecentOrders = ()=>{
	return post('orders/recent');
}

var searchOrders = (query: string)=>{
	return post('orders/search', { query });
}

interface OrderOptions{
	tickets?: boolean,
	payments?: boolean,
	emails?: boolean,
	uses?: boolean,
	concilia?: boolean,
	refunds?: boolean,
	users?: boolean,
}

var getOrder = (order_hash: string, order_id?: number, options: OrderOptions={}, history: boolean=false, cache: boolean=false)=>{
	var payload_data = { 
		order_hash, order_id,
		tickets: options.tickets, 
		payments: options.payments, 
		emails: options.emails,
		uses: options.uses, 
		concilia: options.concilia,
		refunds: options.refunds,
		users: options.users,
		history: history ? 1 : 0,
	}

	if(cache){
		return cachePost('orders/get', `ord-${order_id}`, payload_data);
	}else{
		return post('orders/get', payload_data);
	}
}

var resendEmail = (order_id: number)=>{
	return post('orders/resend', { order_id });
}

var getOrderPdf = (order_id: number)=>{
	return post('orders/pdf', { order_id });
}

interface EditOrder{
	first_name?: string,
	last_name?: string,
	email?: string,
	phone?: string,
	delivery_type?: number,
	payment_method?: number,
	delivery_cost?: number,
	form_answered?: boolean,
	hidden?: boolean,
	delivered?: boolean,
	delivery_id?: number,
	delivery_location?: number,
	reference?: number,
}
var editOrder = (order_id: number, data: EditOrder)=>{
	return post('orders/edit', { order_id, ...data });
}

var convertFree = (order_id: number, comments: string)=>{
	return post('orders/edit/free', { order_id, comments });
}

var cancelOrder = (order_id: number)=>{
	return post('orders/cancel', { order_id });
}

var cancelTicket = (tickets: number[], cancel_reason: CancelReason)=>{
	return post<{
		tickets: number[],
		payments: {
			payment_id: number,
			total_refunded: number,
		}[],
		refunds: {
			refund_id: number,
			refund_type: RefundType,
			amount: number,
		}[]
	}>('orders/ticket/cancel', { tickets, cancel_reason });
}

interface NotUsedRefund{
	payment_id: number,
	amount: number,
	tickets: number[],
}
var cancelNotUsedTickets = (order_id: number)=>{
	return post<{ tickets: number[], refunds: NotUsedRefund[] }>('orders/ticket/cancel/unused', { order_id });
}

var reviveTickets = (order_id: number, tickets: number[])=>{
	return post('orders/revive', { order_id, tickets });
}

var moveTickets = (tickets: number[], move_order: string)=>{
	return post('orders/move', { tickets, move_order });
}

var extendOrder = (order_id: number, seconds: number)=>{
	return post<{
		tickets: { ticket_id: number, paid: boolean, cancelled: boolean, date_expiration: number }[]
	}>('orders/extend', { order_id, seconds })	;
}

var expireOrder = (order_id: number)=>{
	return post<{ tickets: number[] }>('orders/expire', { order_id });
}

var getTicketForm = (ticket_id: number)=>{
	return post('orders/ticket/form', { ticket_id });
}

var editTicketForm = (order_id: number, ticket_id: number, form: FormData)=>{
	form.append('order_id', order_id.toString());
	form.append('ticket_id', ticket_id.toString());
	return post('orders/ticket/form/edit', form);
}

var editTicket = (ticket_id: number, ticket_cost: number, commission: number)=>{
	return post('orders/ticket/edit', {
		ticket_id,
		ticket_cost, commission
	});
}

var getTicketUses = (ticket_id: number)=>{
	return post('orders/ticket/uses', { ticket_id });
}

var ticketUse = (ticket_id: number, date_used: number)=>{
	return post('orders/ticket/use', { ticket_id, date_used });
}

var deleteTicketUse = (ticket_id: number, use_id: number)=>{
	return post('orders/ticket/use/delete', { ticket_id, use_id });
}

var getOrderInvoices = (order_id: number)=>{
	return post('orders/invoices', { order_id });
}

var getOrderEmails = (order_id: number)=>{
	return post('orders/emails', { order_id });
}

var changeUserPassword = (user_id: number, autogenerate: boolean, password: string)=>{
	return post('users/edit/password', { user_id, autogenerate: autogenerate ? 1 : 0, password })
}

var packageConciliaParsedOrders = (orders: (ParsedOrder | InvalidParsedOrder)[])=>{
	return orders.map(a=>(`${(a as ParsedOrder).order_id || ''},${(a as ParsedOrder).payment_id||''},${a.external_id||''},${a.amount.toFixed(2)},${a.date_deposit},${a.deposit_type},${a.payment_method}`));
}

var getConcilia = async (orders: ParsedOrder[], missing: InvalidParsedOrder[], type: number)=>{
	var order_payload = partition(packageConciliaParsedOrders(orders), 200);
	var missing_payload = partition(packageConciliaParsedOrders(missing), 200);

	var orders_concilia : ConciliaDeposit[] = [];
	var missing_concilia : FoundExternalDeposit[] = [];
	var payment_methods : PaymentMethod[] = [];
	for(var i=0; i<Math.max(order_payload.length, missing_payload.length); i++){
		var res = await post<{
			deposits: ConciliaDeposit[],
			missing: FoundExternalDeposit[]
			payment_methods: PaymentMethod[],
		}>('orders/concilia/get', { 
			type,
			orders: order_payload[i],
			missing: missing_payload[i],
		});
		if(res.error) return res;
		orders_concilia.push(...res.data.deposits);
		missing_concilia.push(...res.data.missing);
		payment_methods.push(...res.data.payment_methods.filter(a=>payment_methods.findIndex(b=>b.method_id===a.method_id)==-1));
	}
	return { 
		error: false, 
		data: {
			deposits: orders_concilia,
			missing: missing_concilia,
			payment_methods: res.data.payment_methods
		}
	} as APIResponse<{
		deposits: ConciliaDeposit[],
		missing: FoundExternalDeposit[],
		payment_methods: PaymentMethod[],
	}>;
}

var doConcilia = (orders: ConciliaDeposit[], source_id: number)=>{
	return post('orders/concilia/complete', { 
		source_id,
		orders: packageConciliaParsedOrders(orders)
	});
}

var setTicketHidden = (tickets: number[], hidden: boolean)=>{
	return post('orders/ticket/hidden', { tickets, hidden });
}

var createRefundableRequest = (date_id: number, refundable_web: boolean, refundable_pdv: boolean, refundable_complete: boolean, comments: string)=>{
	return post('events/date/refundable/request', {
		date_id, refundable_web, refundable_pdv, refundable_complete, comments
	});
}

var editDateRefundOptions = (date_id: number, refundable_web: boolean, refundable_pdv: boolean, refundable_complete: boolean, cancel_requests: boolean=false)=>{
	return post('events/date/refundable', {
		date_id, refundable_web, refundable_pdv, refundable_complete, cancel_requests
	});
}

var getDateRefundableRequests = (date_id: number, date: boolean, event: boolean, sales: boolean)=>{
	return post('events/date/refundable/get', {
		date_id, date, event, sales
	});
}

var deleteEventDate = (date_id: number)=>{
	return post('events/date/delete', { date_id });
}

var getDeliveryMethods = (query: string, offset: number=0, count: number=30)=>{
	return post<DeliveryMethod[]>('catalogs/delivery/list', { offset, count, query });
}

var getDeliveryMethod = (delivery_id: number)=>{
	return post<DeliveryMethod>('catalogs/delivery/get', { delivery_id });
}

var getPopularDeliveryMethods = (offset: number, count: number=20)=>{
	return post<DeliveryMethod[]>('catalogs/delivery/popular', { count, offset });
}

var editDelivery = (delivery_id: number, method_name: string, ticket_type: number, needs_location: boolean, delivery_cost: number) => {
	return post('catalogs/delivery/update', { delivery_id, method_name, ticket_type, needs_location, delivery_cost });
}

var createDelivery = (method_name: string, ticket_type: number, needs_location: boolean, delivery_cost: number) => {
	return post<{ delivery_id: number }>('catalogs/delivery/create', { method_name, ticket_type, needs_location, delivery_cost });
}

var getPromoters = (query: string, offset: number=0, count: number=30)=>{
	return post<Promoter[]>(`promoters/list`, { offset, count, query });
}

var getPromoter = (promoter_id: number)=>{
	return post<Promoter>(`promoters/get`, { promoter_id });
}

var setPromoterUserAssigned = (promoter_id: number, user_id: number)=>{
	return post('promoters/assigned', { promoter_id, user_id });
}

interface PromoterEditData{
	promoter_name?: string,
	service_id?: number,
}
var editPromoter = (promoter_id: number, data: PromoterEditData)=>{
	return post('promoters/update', { promoter_id, ...data });
}

var createPromoter = (promoter_name: string) => {
	clearEventsQL(['promoter']);
	return post('promoters/create', { promoter_name });
}

var searchPromoter = (query: string, offset: number=0, count: number=50)=>{
	return post<Promoter[]>('promoters/search', { query, offset, count });
}

var searchPdv = (query: string, offset: number=0, count: number=50)=>{
	return post<PDV[]>('pdv-catalog/list', { query, offset, count });
}

var getEventBalance = (event_id: number, payouts: boolean=true, deposits: boolean=false, force: boolean=false)=>{
	return post('events/balance', {
		event_id, payouts,
		cache: !force,
		deposits,
	})
}

var getPromoterBankAccounts = (promoter_id: number)=>{
	return post(`promoters/bank/list`, { promoter_id });
}

var getPromoterContacts = (promoter_id: number)=>{
	return post(`promoters/contact/list`, { promoter_id });
}

var getPromoterContact = (contact_id: number)=>{
	return post(`promoters/contact/get`, { contact_id });
}

var editPromoterContact = (contact_id: number, contact_name: string, department: string, email: string, phone: string)=>{
	return post(`promoters/contact/update`, { contact_id, contact_name, department, email, phone });
}

var createPromoterContact = (promoter_id: number, contact_name: string, department: string, email: string, phone: string)=>{
	return post<{ contact_id: number }>(`promoters/contact/create`, { promoter_id, contact_name, department, email, phone });
}

var deletePromoterContact = (contact_id: number)=>{
	return post('promoters/contact/delete', { contact_id });
}

var getPromoterEvents = (promoter_id: number, query: string, offset: number=0, count: number=50) => {
	return post(`promoters/event/list`, { promoter_id, query, offset, count });
}

var getPromoterEventsBalance = (promoter_id: number, query: string, date_start: number, date_end: number, offset: number=0, count: number=50) => {
	return post(`promoters/balance/list`, { promoter_id, query, date_start, date_end, offset, count });
}

var getPromoterBalance = (promoter_id: number) => {
	return post<{ balance: number, events: number }>(`promoters/balance`, { promoter_id });
}

interface BankAccountData{
	type: DepositType,
	email: string,
	account_owner: string
	clabe: string,
	card_number: string,
	bank_participant: number,
	rfc: string,
	postal_code: string,
	owner_type: AccountOwnerType,
	account_id?: number,
}
var createPromoterBankAccount = (promoter_id: number, data: BankAccountData)=>{
	return post<BankAccount>(`promoters/bank/create`, {
		promoter_id,
		...data
	});
}

var updatePromoterBankAccount = (data: BankAccountData)=>{
	return post<BankAccount>(`promoters/bank/update`, {
		...data
	});
}

var getPromoterBankAccount = (account_id: number)=>{
	return post(`promoters/bank/get`, { account_id });
}

interface PayoutData{
	payout_type: number,
	account_id?: number,
	date_scheduled?: number,
	comments?: string,
	completed?: boolean,
	dates: {
		date_id: number,
		amount: number,
	}[]
}
var createPayout = (event_id: number, data: PayoutData)=>{
	return post('accounting/payout/create', { event_id, ...data })
}

var getPDVList = (query: string, offset: number=0, count: number=30)=>{
	return post('pdv-catalog/list', { offset, count, query });
}

var getPDV = (pdv_id: number)=>{
	return post('pdv-catalog/get', { pdv_id });
}

export interface PDVEditData{
	pdv_name: string,
	pdv_type: number,
	venue_id: number,
	all_events: boolean,
	location: Location,
	terminal_id: number,
	ignore_limits?: boolean,
	external_terminal?: boolean,
	exclude_commission?: boolean,
	money_supervised?: boolean,
	report_access?: boolean,
}

var createPDV = (data: PDVEditData)=>{
	return post<{ pdv_id: number }>('pdv-catalog/create', data);
}

var editPDV = (pdv_id: number, data: PDVEditData)=>{
	return post('pdv-catalog/update', { pdv_id, ...data });
}

var getPDVCortes = (pdv_id: number, offset=0, count=30) => {
	return post(`pdv-catalog/cortes/list`, { pdv_id, offset, count });
}

var getPDVCorteInfo = (pdv_id: number, corte_id: number,) => {
	return post(`pdv-catalog/cortes/get`, { pdv_id, corte_id });
}

var closePDVCorte = (pdv_id: number, corte_id: number,) => {
	return post(`pdv-catalog/cortes/finish`, { pdv_id, corte_id });
}

var createPDVCorte = (pdv_id: number) => {
	return post<{ corte_id: number, user_start: number }>(`pdv-catalog/cortes/create`, { pdv_id });
}

var getPDVAcces = (pdv_id: number, offset=0, count=30) => {
	return post(`pdv-catalog/access/list`, { pdv_id, offset, count });
}

var getPDVZones = (pdv_id: number, offset=0, count=30, query?: string) => {
	return post(`pdv-catalog/zone/list`, { pdv_id, offset, count, query });
}

var savePDVZone = (pdv_id: number, zone_id: number) => {
	return post(`pdv-catalog/zone/save`, { pdv_id, zone_id });
}

var deletePDVZone = (pdv_id: number, zone_id: number) => {
	return post(`pdv-catalog/zone/delete`, { pdv_id, zone_id });
}

var getPDVEvents = (pdv_id: number, offset=0, count=30, query?: string) => {
	return post(`pdv-catalog/events/list`, { pdv_id, offset, count, query });
}

var savePDVEvent = (pdv_id: number, event_id: number) => {
	return post(`pdv-catalog/events/save`, { pdv_id, event_id });
}

var deletePDVEvent = (pdv_id: number, event_id: number) => {
	return post(`pdv-catalog/events/delete`, { pdv_id, event_id });
}

const getPDVAccesUser = (user_id: number, offset: number=0, count=30) => {
	return post(`pdv-catalog/user/list`, { user_id, offset, count });
}

var reinitializePdv = (pdv_id: number)=>{
	return post<{ token: string }>('pdv-catalog/reinitialize', { pdv_id });
}

var getUserLogins = (user_id: number, offset=0, count=30) => {
	return post(`users/logins`, { user_id, offset, count });
}

var getPayouts = (payouts: boolean, pending: boolean, approval: boolean, completed: boolean, groups: boolean, offset: number=0, count: number=20)=>{
	return post<{
		pending?: ListPayout[],
		approval?: ListPayout[],
		completed?: ListPayout[],
		groups?: {
			pending: ListPayoutGroup[],
			completed: ListPayoutGroup[],
		}
	}>('accounting/payout/list', {
		payouts,
		pending,
		approval,
		completed,
		offset, 
		groups,
		count
	});
}

var approvePayouts = (payouts: number[])=>{
	return post<{ approved: number[], completed: number[] }>('accounting/payout/approve', { payouts });
}

var completePayouts = (payouts: number[])=>{
	return post<{ completed: number[] }>('accounting/payout/complete', { payouts });
}

var rejectPayouts = (payouts: number[], feedback: string)=>{
	return post<{ rejected: number[] }>('accounting/payout/reject', { payouts, feedback });
}

var resetPayouts = (payouts: number[])=>{
	return post<{ rejected: number[] }>('accounting/payout/reset', { payouts });
}

var createPayoutGroup = (payouts: number[], bank_id: number, date_scheduled: number)=>{
	return post<{ group_id: number }>('accounting/payout/group/create', { payouts, bank_id, date_scheduled });
}

var getPayoutGroup = (group_id: number)=>{
	return post('accounting/payout/group/get', { group_id });
}

var downloadPayoutPoliza = async (payouts: number[], group_id?: number, date_override: number=null) : Promise<APIResponse<any>>=>{
	var res = (await post('accounting/payout/download/poliza', { payouts, group_id, date_override }, {
		response_type: 'blob'
	}) as any) as Blob;
	if(res.type==='application/json'){
		return JSON.parse(await res.text());
	}

	const link = document.createElement('a');
	const url = URL.createObjectURL(res);
	link.href = url;
	link.download = `PolizaLiq-${group_id || payouts.join(',')}.txt`;
	link.click();
	link.remove();

	return {
		error: false,
		code: null,
		message: null,
		data: null
	};
}

var downloadPayoutSTP = async (payouts: number[]) : Promise<APIResponse<any>>=>{
	var res = (await post('accounting/payout/stp/txt', { payouts }, {
		response_type: 'blob'
	}) as any) as Blob;
	if(res.type==='application/json'){
		return JSON.parse(await res.text());
	}

	const link = document.createElement('a');
	const url = URL.createObjectURL(res);
	link.href = url;
	link.download = `STPLiq-${payouts.join(',')}.txt`;
	link.click();
	link.remove();

	return {
		error: false,
		code: null,
		message: null,
		data: null
	};
}

var downloadPayoutZip = async (payouts: number[], date_override?: number) : Promise<APIResponse<any>>=>{
	var res = (await post(`accounting/payout/stp/zip${date_override ? 'd='+moment.unix(date_override).format('DD-MM-YYYY') : ''}`, { payouts }, {
		response_type: 'blob'
	}) as any) as Blob;
	if(res.type==='application/json'){
		return JSON.parse(await res.text());
	}

	var date = (date_override ? moment.unix(date_override) : moment()).format('YYYY-MM-DD')

	const link = document.createElement('a');
	const url = URL.createObjectURL(res);
	link.href = url;
	link.download = `STPLiq-${date}.zip`;
	link.click();
	link.remove();

	return {
		error: false,
		code: null,
		message: null,
		data: null
	};
}

var deleteAsset = (asset_id: number, data: AssetRelations)=>{
	return post('accounting/assets/delete', { asset_id, data });
}

var uploadAsset = (file: File, type_id: AssetType, data: AssetRelations)=>{
	var fdata = new FormData();
	fdata.append('type_id', type_id.toString());
	fdata.append('data', JSON.stringify(data));
	fdata.append('asset', file);
	
	return post<AssetFile>('accounting/assets/upload', fdata);
}

var getPayoutAssets = (payout_id: number)=>{
	return post('accounting/payout/assets/list', { payout_id });
}

const getScannerList = (query: string, offset: number=0, count: number=30)=>{
	return post('catalogs/scanner/list', { query, offset, count});
}

const getScanner = (scanner_id: number)=>{
	return post<Scanner>('catalogs/scanner/get', { scanner_id });
}

const createScanner = (scanner: Scanner, events: number[], sections: number[])=>{
	return post<{ scanner_id: number }>('catalogs/scanner/create', { scanner, events, sections });
}

const editScanner = (scanner: Scanner, events: number[], sections: number[])=>{
	return post<{ scanner_id: number }>('catalogs/scanner/update', { scanner, events, sections });
}

var getPayout = (payout_id: number, evidence: boolean=false, deposits: boolean=true)=>{
	return post('accounting/payout/get', { payout_id, evidence, deposits });
}

const getCouponList = (event_id: number, query: string, offset: number=0, count: number=30)=>{
	return post('events/coupon/list', { event_id, query, offset, count });
}

const getCoupon = (coupon_id: number)=>{
	return post('events/coupon/get', { coupon_id });
}

const createCoupon = (coupon: Coupon, dates: number[], prices: number[])=>{
	return post<{ coupon_id: number }>('events/coupon/create', { coupon, dates, prices });
}

const deleteCoupon = (coupon_id: number)=>{
	return post('events/coupon/delete', { coupon_id });
}

const editCoupon = (coupon_id: number, coupon: Coupon, dates: number[], prices: number[])=>{
	return post<{ coupon_id: number }>('events/coupon/update', { coupon_id, coupon, dates, prices });
}

var deleteDeposit = (deposit_id: number)=>{
	return post('orders/concilia/delete', { deposit_id });
}

var createDeposit = (payment_id: number, amount: number, date_deposit: number, deposit_type: number, external_id?: string)=>{
	return post<{ deposit_id: number }>('orders/concilia/create', { payment_id, amount, deposit_type, date_deposit, external_id });
}

var getRefunds = (pending: boolean, completed: boolean, offset: number=0, count: number=20)=>{
	return post<{ pending?: OrderRefund[],  completed?: OrderRefund[] }>('refund/list', {
		pending,
		completed,
		offset, 
		count
	});
}

const getRefund = (refund_id: number)=>{
	return post('refund/get', { refund_id });
}

var completeRefunds = (refunds: number[])=>{
	return post<{ completed: number[] }>('refund/complete', { refunds });
}

var completeRefundPayments = (refund_id: number, payments: number[])=>{
	return post('refund/payment/complete', { refund_id, payments });
}

var revertRefundPayments = (refund_id: number, payments: number[])=>{
	return post('refund/payment/revert', { refund_id, payments });
}

var cancelRefunds = (refunds: number[])=>{
	return post<{ refunds: number[] }>('refund/cancel', { refunds });
}

var pendingRefunds = (refunds: number[])=>{
	return post<{ refunds: number[] }>('refund/pending', { refunds });
}

var uploadRefundEvidence = async (refund_id:number, file: File) => {
	var data = new FormData();
	data.append('refund_id', refund_id.toString());
	data.append('evidence', file);

	return post<{ date: number, filename: string, link: string }>('refund/evidence/upload', data);
}

var removedRefundEvidence = async (refund_id:number, filename: string) => {
	return post('refund/evidence/remove', { refund_id, filename });
}

var downloadRefundZip = async (refunds: number[], date_override?: number) : Promise<APIResponse<any>>=>{
	var res = (await post(`refund/stp/zip${date_override ? 'd='+moment.unix(date_override).format('DD-MM-YYYY') : ''}`, { refunds }, {
		response_type: 'blob'
	}) as any) as Blob;
	if(res.type==='application/json'){
		return JSON.parse(await res.text());
	}

	var date = (date_override ? moment.unix(date_override) : moment()).format('YYYY-MM-DD')

	const link = document.createElement('a');
	const url = URL.createObjectURL(res);
	link.href = url;
	link.download = `STPReembolsos-${date}.zip`;
	link.click();
	link.remove();

	return {
		error: false,
		code: null,
		message: null,
		data: null
	};
}

var getEventGroups = (query: string, offset: number=0, count: number=30)=>{
	return post('parent/list', { query, offset, count });
}

const getEventGroup = (group_id: number)=>{
	return post('parent/get', { group_id });
}

export interface EventGroupEdit{
	group_name: string,
	description: string,
	subdomain: string,
	category_id: number,
	published: boolean,
	date_publish?: number,
}

const createEventGroup = (data: EventGroupEdit, events: number[])=>{
	return post<{ group_id: number }>('parent/create', { ...data, events });
}

const editEventGroup = (group_id: number, data: EventGroupEdit, events: number[])=>{
	return post<{ group_id: number }>('parent/update', { group_id, ...data, events });
}

var setEventGroupPoster = (group_id: number, file: File)=>{
	var data = new FormData();
	data.append('group_id', group_id.toString());
	data.append('poster', file);
	return post<boolean>('parent/poster', data)
}

var getTicketTemplates = (query: string, offset: number=0, count: number=10)=>{
	return post('tools/templates/list', { query, offset, count });
}

var getTicketTemplate = (ticket_id: number)=>{
	return post('tools/templates/get', { ticket_id });
}

var getTemplateTest = (zpl: string)=>{
	return post('tools/templates/test', { zpl });
}

var isSeatDeletable = (seat_id: number[])=>{
	return post<{ seat_id: number, deletable: boolean }[]>('venues/section/deletableseat', { seat_id });
}

var updateTicketSeat = (ticket_id: number, seat_id: number, price_id?: number)=>{
	return post('orders/ticket/seat', { ticket_id, price_id, seat_id })
}

var deleteSection = (section_id: number)=>{
	return post<boolean>('venues/section/delete', { section_id })
}

var getDateSectionHolders = (date_id: number, section_id: number)=>{
	return post('events/date/section/hold/list', { date_id, section_id });
}

var createSectionHold = (date_id: number, section_id: number, amount: number, comments: string) => {
	return post<{ hold_id: number }>('events/date/section/hold/create', {
		date_id, section_id,
		amount, comments
	});
}

var updateSectionHold = (hold: SectionHold) => {
	return post('events/date/section/hold/update', {
		hold_id: hold.hold_id,
		amount: hold.amount,
		comments: hold.comments,
	});
}

var deleteSectionHold = (hold_id: number) => {
	return post('events/date/section/hold/delete', { hold_id });
}

var getUsers = (users: number[])=>{
	return post<{
		user_id: number,
		email: string,
		first_name: string,
		last_name: string,
	}[]>('users/list/names', { users });
}

interface RefundOptions{
	commission?: boolean,
	delivery?: boolean,
	payment?: boolean,
	email?: string,
	completed?: boolean,
	send_email?: boolean,
}

var createRefund = (order_id: number, tickets: number[], options: RefundOptions)=>{
	return post<{
		refund: OrderRefund,
		payments?: {
			payment_id: number,
			amount: number,
		}[],
		tickets?: {
			ticket_id: number,
			amount_refunded: number,
			commission_refunded: number,
		}[],
	}>('orders/refund', { order_id, tickets, ...options });
}

var viewRefundRequest = (refund_id: number)=>{
	return post<{ request_url: string }>('refund/request/view', { refund_id });
}

var resendRefundRequest = (refund_id: number, email: string)=>{
	return post('refund/request/resend', { refund_id, email });
}

var getPapeletaDays = (days: number=60)=>{
	return post<{ date: string, deposits: number }[]>('accounting/papeleta/days', { days });
}

var getPapeletaDay = (day: string, day_end?: string)=>{
	return post<PapeletaData>('accounting/papeleta/day', { day, day_end });
}

var getPapeletaPdv = (date: number)=>{
	return post<PapeletaPdvData[]>('accounting/papeleta/pdv', { date });
}

var downloadPapeletaZip = async (date: number, separate_commission: boolean) : Promise<APIResponse<any>>=>{
	var res = (await post(`accounting/papeleta/download`, { date, separate_commission }, {
		response_type: 'blob'
	}) as any) as Blob;
	if(res.type==='application/json'){
		return JSON.parse(await res.text());
	}

	var day = fromUnixLocal(date);

	const link = document.createElement('a');
	const url = URL.createObjectURL(res);
	link.href = url;
	link.download = `Papeleta-${day.toFormat('yyyyMMdd')}.zip`;
	link.click();
	link.remove();

	return {
		error: false,
		code: null,
		message: null,
		data: null
	};
}

var downloadPdvDeposits = async (date: number)=>{
	var res = (await post(`accounting/papeleta/download/pdv`, { date }, {
		response_type: 'blob'
	}) as any) as Blob;
	if(res.type==='application/json'){
		return JSON.parse(await res.text());
	}

	var day = fromUnixLocal(date);

	const link = document.createElement('a');
	const url = URL.createObjectURL(res);
	link.href = url;
	link.download = `PapeletaTaquillas-${day.toFormat('yyyyMMdd')}`;
	link.click();
	link.remove();

	return {
		error: false,
		code: null,
		message: null,
		data: null
	};
}

var getEventOrdersConcilia = (event_id: number, method_id: number, query: string, offset: number=0, count: number=30)=>{
	return post('events/conciliacion', { event_id, method_id, query, offset, count });
}

var getInvoices = (date_start: number, date_end: number, offset: number=0, count: number=30)=>{
	return post<ReportResponse<{
		invoice_id: number,
		order_hash: number,
		legal_name: string,
		rfc: string,
		amount: number,
	}>>('reports/invoices', { date_start, date_end, offset, count });
}

var getIncomeReport = (date_start: number, date_end: number)=>{
	return post<ReportResponse<{
		event_id: number,
		event_name: string,
		promoter_id: number,
		promoter_name: string,
		user_assigned: number,
		user_assigned_name: string,
		ticket_income: number,
		commission: number,
	}>>('reports/income', { date_start, date_end });
}

var getEventSalesReport = (event_id: number, dates: number[], text: boolean=false)=>{
	return post<{ url: string }>('reports/event/sales', { event_id, dates, text });
}

var getEventPdvSales = (event_id: number, dates: number[])=>{
	return post<{ url: string }>('reports/event/sales/pdv', { event_id, dates });
}

var getDateSeatDump = (date_id: number)=>{
	return post<{ url: string }>('reports/event/seats/dump', { date_id });
}

var getPaymentInfo = (payment_id: number) => {
	return post('orders/payment', { payment_id });
}

var updatePayment = (payment_id: number, payment_cost: number, delivery_cost: number, total: number) => {
	return post('orders/payment/update', { payment_id, payment_cost, delivery_cost, total });
}

var getLocationInfo = (location_id: number) => {
	return post('orders/location', { location_id });
}

var updateLocation = (location: Location) => {
	return post('orders/location/update', { location });
}

var createLocation = (location: Location) => {
	return post('orders/location/create', { location });
}

var updateOrderLocation = (order_id: number, location_id: number) => {
	return post('orders/location/order', { order_id, location_id });
}

var sendRefundInfoRequest = (refund_id: number, email: string)=>{
	return post('refund/payment/info', { refund_id, email });
}

var getDatePaymentsList = (start_date: number, days: number) => {
	return post('reports/payments/list', { start_date, days });
}

var getDatePayments = (day: string) => {
	return post('reports/payments/get', { day });
}

var resendOrderEmail = (order_id: number, email_id: number) => {
	return post('orders/emails/resend', { order_id, email_id });
}

var setDateQueue = (date_id: number, queue: boolean)=>{
	return post<{ queue_id: number }>('events/date/queue', { date_id, queue });
}

var reinitializeScanner = (scanner_id: number)=>{
	return post('catalogs/scanner/reinitialize', { scanner_id });
}

var resetScannerToken = (scanner_id: number)=>{
	return post<{ token: string }>('catalogs/scanner/token', { scanner_id });
}

var getEventDateForms = (event_id: number)=>{
	return post('events/date/forms/list', { event_id });
}

var getEventDateQuestions = (date_id: number)=>{
	return post('events/date/forms/questions', { date_id });
}

var createDateQuestion = (date_id: number, question: { question_type: number, question: string, active: boolean,comment: string, default_value: string, required: boolean,values: any[] })=>{
	return post('events/date/forms/question/save', { date_id, question });
}

var updateDateQuestion = (question: { question_id: number, question_type: number, question: string, active: boolean,comment: string, default_value: string, required: boolean,values: any[] })=>{
	return post('events/date/forms/question/update', { question });
}

var deleteQuestion = (question_id: number) => {
	return post('events/date/forms/question/delete', { question_id });
}

var getEventDateQuestion = (question_id: number)=>{
	return post('events/date/forms/question', { question_id });
}

var updateQuestionOrder = (questions : { question_id: number, sort_order: number }[])=>{
	return post('events/date/forms/question/order', { questions });
}

var createQuestionValue = (option: { question_id: number, value: string, active: boolean })=>{
	return post('events/date/forms/question/value/save', { option });
}

var updateQuestionValue = (option: { value_id: number, value: string, active: boolean })=>{
	return post('events/date/forms/question/value/update', { option });
}

var deleteQuestionValue = (value_id: number) => {
	return post('events/date/forms/question/value/delete', { value_id });
}

var updateQuestionValueOrder = (values : { value_id: number, sort_order: number }[])=>{
	return post('events/date/forms/question/value/order', { values });
}

var cloneUser = ( user: { email: string, first_name: string, last_name: string, id_original_user: number }) => {
	return post('users/clone', { user });
}

var refreshUserSecret = (user_id: number) => {
	return post('users/secret', { user_id });
}

const getZonesList = (query: string, offset: number=0, count: number=30)=>{
	return post<Zone[]>('catalogs/zones/list', { query, offset, count});
}

const getPopularZones = (offset: number=0, count: number=20)=>{
	return post<Zone[]>('catalogs/zones/popular', { offset, count});
}

const getZone = (zone_id: number)=>{
	return post<Zone>('catalogs/zones/get', { zone_id });
}

const createZone = (zone_name: string, description: string)=>{
	return post<{ zone_id: number }>('catalogs/zones/create', { zone_name, description });
}

const editZone = (zone_id: number, zone_name: string, description: string)=>{
	return post<{ success: boolean }>('catalogs/zones/update', { zone_id, zone_name, description });
}

var searchZone = (query: string, offset: number=0, count: number=50)=>{
	return post<Zone[]>('catalogs/zones/search', { query, offset, count });
}

var getZoneEventsList = (zone_id: number, offset=0, count=30) => {
	return post(`catalogs/zones/events/list`, { zone_id, offset, count });
}

var getPaymentPlans = () => {
	return post<PaymentPlan[]>('catalogs/paymentplan/get');
}

var getEventPaymentPlans = (event_id: number) => {
	return post<EventPaymentPlan[]>('events/paymentplan/get', { event_id });
}

var saveEventPaymentPlan = (event_id: number, plan_id: number) => {
	return post('events/paymentplan/save', { event_id, plan_id });
}

var deleteEventPaymentPlan = (event_id: number, plan_id: number) => {
	return post('events/paymentplan/delete', { event_id, plan_id });
}

var getBankBinList = (query: string, offset: number=0, count: number=30)=>{
	return post<BankBin[]>('catalogs/bank/list', { query, offset, count});
}

var getBank = (bank_id: number)=>{
	return post<BankBin>('catalogs/bank/get', { bank_id });
}

var createBankBin = (bank_name: string, description: string)=>{
	return post<{ bank_id: number }>('catalogs/bank/create', { bank_name, description });
}

var editBankBin = (bank_id: number, bank_name: string, description: string)=>{
	return post('catalogs/bank/update', { bank_id, bank_name, description });
}

var addBinNumbers = (bank_id: number, bines: string[])=>{
	return post('catalogs/bank/bin/add', { bank_id, bines });
}

var clearBinNumbers = (bank_id: number)=>{
	return post('catalogs/bank/clear', { bank_id });
}

var deleteBinNumber = (number_id: number)=>{
	return post('catalogs/bank/bin/delete', { number_id });
}

var deleteBank = (bank_id: number)=>{
	return post('catalogs/bank/delete', { bank_id });
}

var getPdvConciliaList = (query: string, all: boolean, offset: number=0, count: number=30) => {
	return post('accounting/pdv/list', { query, all, offset, count});
}

var getPdvStats = (pdv_id: number) => {
	return post<PdvStats>('accounting/pdv/get', { pdv_id });
}

var getPdvConciliaDeposits = (pdv_id: number, query: string, offset: number=0, count: number=30) => {
	return post('accounting/pdv/deposit/list', { pdv_id, query, offset, count });
}

var getPdvConciliaCortes = (pdv_id: number, query: string, cash_only: boolean=false, offset: number=0, count: number=30) => {
	return post('accounting/pdv/corte/list', { pdv_id, query, cash_only, offset, count });
}

// var getPdvCorteOrders = (corte_id: number, query: string, offset: number=0, count: number=30) => {
// 	return post('accounting/pdv/corte/orders', { corte_id, query, offset, count });
// }

var getPdvCorteDeposits = (corte_id: number, query: string, offset: number=0, count: number=30) => {
	return post('accounting/pdv/corte/deposits', { corte_id, query, offset, count });
}

var getPdvCorteDetail = (corte_id: number, summary: boolean, deposits_offset: number=0, deposits_count: number=30, orders_offset: number=0, orders_count: number=30) => {
	return post('accounting/pdv/corte/get', { corte_id, summary, deposits_offset, deposits_count, orders_offset, orders_count });
}

interface CreateDepositData{
	date_deposit: number,
	source_id: number,
	deposit_type: number,
	description: string,
	cortes: {
		corte_id: number,
		amount: number,
	}[],
	dates?: {
		date_id: number,
		amount: number,
	}[]
}
var createPdvDeposit = async (pdv_id: number, data: CreateDepositData) => {
	var res = await post<{ deposit_id: number, payout_id: number }>('accounting/pdv/deposit/create', {
		pdv_id, ...data
	});
	return res;
}

// var updatePdvDeposit = (deposit_id:number, cortes: { corte_id: number, amount: number }[], files: File[]) => {
// 	var data = new FormData();
// 	data.append('data', JSON.stringify({ deposit_id, cortes }));
// 	for (let index = 0; index < files.length; index++) {
// 		data.append(`evidence-${index}`, files[index]);
// 	}
// 	return post('accounting/pdv/deposit/update', data);
// }

var deletePdvDeposit = (deposit_id: number)=>{
	return post<boolean>('accounting/pdv/deposit/delete', { deposit_id })
}

var verifyPdvDeposit = (deposit_id: number)=>{
	return post<boolean>('accounting/pdv/deposit/verify', { deposit_id })
}

var getPdvDeposit = (deposit_id: number) => {
	return post<PDVDeposit>('accounting/pdv/deposit/get', { deposit_id });
}

var getPdvDepositSource = () => {
	return cachePost<PdvDepositSource[]>('catalogs/sources/all', 'pdvdpsrc');
}

var getPdvDepositTypes = () => {
	return cachePost<PDVDepositType[]>('catalogs/pdv/deposit/types', 'pdvdptyp');
}

var getPdvOrderConcilia = (corte_id: number) => {
	return post('accounting/pdv/order/concilia', { corte_id });
}

var saveDepositPayment = (date_deposit: number, payments: { payment_id: number, amount: number }[]) => {
	return post('accounting/pdv/order/concilia/save', { date_deposit, payments });
}

var getPaymentTerminalList = (query: string, offset: number=0, count: number=30)=>{
	return post<PaymentTerminal[]>('catalogs/terminal/list', { query, offset, count});
}

var getPaymentTerminal = (terminal_id: number)=>{
	return post<PaymentTerminal>('catalogs/terminal/get', { terminal_id });
}

var createPaymentTerminal = (terminal_name: string, terminal_type: number, license_id?: number)=>{
	return post<{ terminal_id: number }>('catalogs/terminal/create', { terminal_name, terminal_type, license_id });
}

var editPaymentTerminal = (terminal_id: number, terminal_name: string, terminal_type: number)=>{
	return post('catalogs/terminal/update', { terminal_id, terminal_name, terminal_type });
}

var editTerminalSerial = (terminal_id: number, terminal_serial: string)=>{
	return post('catalogs/terminal/serial', { terminal_id, terminal_serial });
}

var deletePaymentTerminal = (terminal_id: number)=>{
	return post('catalogs/terminal/delete', { terminal_id });
}

var reinitializeTerminal = (terminal_id: number)=>{
	return post<{ token: string }>('catalogs/terminal/reinitialize', { terminal_id });
}

var editLicenseTerminal = (terminal_id: number, license_id: number)=>{
	return post('catalogs/terminal/license', { terminal_id, license_id });
}

var getLicenses = ()=>{
	return post<PaymentTerminalLicense[]>('catalogs/terminal/license/list');
}

var getUserFromEmail = (email: string)=>{
	return post<ShortUser>('users/get/email', { email });
}

var updateUserSystemAccess = (user_id: number, system_id: SystemAccess, active: boolean)=>{
	return post<boolean>('users/systems/update', { user_id, system_id, active });
}

var getPaymentConcilia = (payment_id: number)=>{
	return post<ExternalSearchOrder>('accounting/concilia/payment/get', { payment_id });
}

var getAllReportsAccess = ()=>{
	return cachePost<ReportsAccessType[]>('users/reports/access/all', 'RPALLACC');
}

var addUserReportsEvent = (user_id: number, event_id: number, access: number[])=>{
	return post('users/reports/event/add', { user_id, event_id, access });
}

var addUserReportsPromoter = (user_id: number, promoter_id: number, access: number[])=>{
	return post('users/reports/promoter/add', { user_id, promoter_id, access });
}

var removeUserReportsEvent = (user_id: number, event_id: number)=>{
	return post('users/reports/event/remove', { user_id, event_id });
}

var removeUserReportsPromoter = (user_id: number, promoter_id: number)=>{
	return post('users/reports/promoter/remove', { user_id, promoter_id });
}

var getUserReportsAccess = (user_id: number)=>{
	return post<{
		events: UserReportsEvent[],
		promoters: UserReportsPromoter[],
	}>('users/reports/access/get', { user_id });
}

var getBusinessBanks = async (query?: string)=>{
	var res = await cachePost<BusinessBank[]>('catalogs/business/banks/get', 'BSNBKS');
	if(res.error) return res;
	if(query){
		return {
			...res,
			data: res.data.filter(a=>removeAccents(a.bank_name).toUpperCase().indexOf(removeAccents(query).toUpperCase())!=-1)
		} as APIResponse<BusinessBank[]>
	}else return res;
}

var getAcquirerService = async (query?: string)=>{
	var res = await cachePost<AcquirerService[]>('promoters/service/list', 'ACQSRVLST');
	if(res.error) return res;
	if(query){
		return {
			...res,
			data: res.data.filter(a=>removeAccents(a.service_name).toUpperCase().indexOf(removeAccents(query).toUpperCase())!=-1)
		} as APIResponse<AcquirerService[]>
	}else return res;
}

var approvePayoutGroup = (groups: number[])=>{
	return post('accounting/payout/group/approve', { groups });
}

var rejectPayoutGroup = (groups: number[], feedback: string)=>{
	return post('accounting/payout/group/reject', { groups, feedback });
}

var cancelPayoutGroup = (group_id: number)=>{
	return post('accounting/payout/group/cancel', { group_id });
}

var setPaymentPaid = (payment_id: number)=>{
	return post<{ tickets: number[] }>('orders/payment/paid', { payment_id });
}

var getOrderCreatorDate = (date_id: number)=>{
	return post('orders/creator/date', { date_id });
}

var executeRefund = (refund_id: number, payment_id: number)=>{
	return post<{
		payment_id: number,
		completed: boolean
	}>('refund/execute', { refund_id, payment_id });
}

var getCorteDates = (pdv_id: number, cortes: number[])=>{
	return post('accounting/pdv/corte/dates', { pdv_id, cortes });
}

var getAmexAccounting = (payments: AccountingAmexPayment[])=>{
	return post<PayfrontAccountingPayment[]>('accounting/concilia/amex', { payments });
}

interface CreateTicketPaymentData{
	payment_method: number,
	payment_id: number,
	gift_description: string,
	delivery_cost: number,
}
var createTicketsPayment = (order_id: number, tickets: number[], data: CreateTicketPaymentData)=>{
	return post<{
		order_id: number,
		payment_id: number,
		payment_method: number,
		total: number,
		tickets: number[],
	}>('orders/tickets/paid', { order_id, tickets, ...data });
}

interface CreateOrderData{
	cart: {
		price_id: number,
		seat_id: number,
		quantity: number
	}[],
	payment_method: number,
	expiration: number,
	order_hash: string,
	delivery_id: number,
	email: string,
	comments?: string,
	no_commission?: boolean,
}
var createOrder = (data: CreateOrderData)=>{
	return post<{
		order_id: number,
		order_hash: string,
	}>('orders/create', data);
}

var getPdvPaymentMethods = (pdv_id: number)=>{
	return post('pdv-catalog/methods/get', { pdv_id });
}

interface PDVMethodsData{
	type_id: number,
	force_disable: boolean,
	force_priority?: boolean,
	commission_percent: number,
	commission_amount: number,
}
var editPdvPaymentMethods = (pdv_id: number, methods: PDVMethodsData[])=>{
	return post('pdv-catalog/methods/update', { pdv_id, methods });
}

var editDatePdvMethods = (date_id: number, methods: PDVMethodsData[])=>{
	return post('events/date/payment/pdv', { date_id, methods });
}

var uploadEventMap = (event_id: number, file: File)=>{
	var fdata = new FormData();
	fdata.append('event_id', event_id.toString());
	fdata.append('map', file);
	
	return post<AssetFile>('events/map', fdata);
}

var removeEventMap = (event_id: number)=>{
	return post('events/map/remove', { event_id });
}

var getEventSections = (event_id: number)=>{
	return post('events/sections/list', { event_id })
}

var getEventSectionPrices = (event_id: number, section_id: number)=>{
	return post(`events/sections/prices/list`, { event_id, section_id });
}

var getPDVSalesLog = (pdv_id: number, date_start: number, date_end: number)=>{
	return post<{ url: string }>('reports/pdv/sales', { pdv_id, date_start, date_end });
}

var getPdvCorteLog = (pdv_id: number, event_id: number, date_start: number, date_end: number)=>{
	return post<{ url: string }>('reports/pdv/cortes/event', { pdv_id, event_id, date_start, date_end });
}

var getPdvTicketLog = (pdv_id: number, event_id: number, date_start: number, date_end: number)=>{
	return post<{ url: string }>('reports/pdv/tickets', { pdv_id, event_id, date_start, date_end });
}

var getEventListReport = (date_start: number, date_end: number)=>{
	return post<ReportResponse<{
		event_id: number,
		event_name: string,
		promoter_id: number,
		promoter_name: string,
		commission_return: number,
		dates: {
			date_id: number,
			date: number,
			venue_id: number,
			venue_name: string,
			city: string,
			ticket_count: number,
			has_infinite: boolean,
			tickets_sold: number,
			ticket_cost: number,
			commission: number,
		}[]
	}>>('reports/events/list', { date_start, date_end });
}

var getSystemStats = (date_start: number, date_end: number)=>{
	var offset = getNowLocal().offset
	return post('reports/stats/get', { date_start, date_end, offset });
}

var getPdvCortesReport = (date: number)=>{
	return post('reports/pdv/cortes', { date });
}

var getPdvSalesReport = (date_start: number, date_end: number)=>{
	return post('reports/pdv/ticket/sales', { date_start, date_end });
}

var getTicketPrints = (ticket_id: number)=>{
	return post('orders/ticket/print/list', { ticket_id });
}

var cancelTicketPrint = (print_id: number)=>{
	return post('orders/ticket/print/cancel', { print_id });
}

var listActiveQueues = ()=>{
	return post('tools/queues/list');
}

var setQueueConcurrent = (queue_id: number, concurrent: number)=>{
	return post('tools/queues/concurrent', { queue_id, concurrent });
}

var setQueuePaused = (queue_id: number, paused: boolean)=>{
	return post('tools/queues/paused', { queue_id, paused });
}

var getEventDepositSources = (event_id: number)=>{
	return post('accounting/concilia/sources', { event_id });
}

var getDateDepositSources = (date_start: number, date_end: number, system: number)=>{
	return post<EventPaymentSources[]>('accounting/concilia/sources/dates', { date_start, date_end, system });
}

var getResetPasswordLink = (user_id: number)=>{
	return post<{ url: string }>('users/password/link', { user_id });
}

var getEventBusinessSources = (event_id: number)=>{
	return post<EventBusinessSource[]>('events/balance/business', { event_id });
}

var getEventOrders = (event_id: number, config: EventOrderConfig, offset: number=0, count: number=50)=>{
	return post<{
		count: { tickets: number, orders: number },
		orders: EventOrder[]
	}>('events/orders', {
		event_id, config, offset, count
	});
}

var refreshUserEvents = (user_id: number)=>{
	return post('users/reports/refresh', { user_id });
}

var getPayoutsReport = (date_start: number, date_end: number)=>{
	return post('reports/events/payouts', { date_start, date_end });
}

var getDateBalanceReport = (date_start: number, date_end: number)=>{
	return post('reports/events/balance', { date_start, date_end });
}

var getCityBuyers = async (date_start: number, date_end: number, city: string)=>{
	var res = (await post(`reports/events/buyers`, { date_start, date_end, city }, {
		response_type: 'blob'
	}) as any) as Blob;
	if(res.type==='application/json'){
		return JSON.parse(await res.text());
	}

	var ds = fromUnixLocal(date_start);
	var de = fromUnixLocal(date_end);

	const link = document.createElement('a');
	const url = URL.createObjectURL(res);
	link.href = url;
	link.download = `Compradores-${city}_${ds.toFormat('MMyyyy')}-${de.toFormat('MMyyyy')}.`;
	link.click();
	link.remove();

	return {
		error: false,
		code: null,
		message: null,
		data: null
	};
}

var removeEventPoster = (event_id: number)=>{
	return post('events/poster/remove', { event_id });
}

var changeTicketPrice = (ticket_id: number, price_id: number)=>{
	return post('orders/ticket/move', { ticket_id, price_id });
}

var getEventCodes = (event_id: number)=>{
	return post<EventCodeList[]>('events/codes/list', { event_id });
}

var createEventCodes = (event_id: number, codes: EventCodeCreate)=>{
	return post<{ group_id: number }>('events/codes/create', { event_id, codes });
}

var getEventCodeGroup = (event_id: number, group_id: number, count: number=50)=>{
	return post<EventCodeGroup>('events/codes/get', { event_id, group_id, count });
}

var downloadLink = (url: string, filename: string)=>{
	const link = document.createElement('a');
	link.href = url;
	link.download = filename;
	link.click();
	link.remove();
}

var downloadEventCodes = async (group_id: number, all: boolean, used: boolean) : Promise<APIResponse<{ url: string }>>=>{
	var res = (await post(`events/codes/download`, { group_id, all, used }, {
		response_type: 'blob'
	}) as any) as Blob;
	if(res.type==='application/json'){
		return JSON.parse(await res.text());
	}

	const url = URL.createObjectURL(res);
	return {
		code: null,
		error: false,
		message: null,
		data: { url }
	}
}

var getSystemConfig = ()=>{
	return post<SystemConfig>('tools/config')
}

var setSystemConfig = (config: SystemConfig)=>{
	return post('tools/config/update', { config });
}

export default {
	// setOrderPaid,
	addDateDelivery,
	addDatePaymentMethod,
	addDateSection,
	addPrice,
	addUserReportsEvent,	
	addUserReportsPromoter,
	approvePayoutGroup,
	approvePayouts,
	cancelNotUsedTickets,
	cancelOrder,
	cancelPayoutGroup,
	cancelRefunds,
	cancelTicket,
	cancelTicketPrint,
	changeTicketPrice,
	changeUserPassword,
	clearBinNumbers,
	cloneUser,
	cloneVenueSection,
	closePDVCorte,
	completePayouts,
	completeRefundPayments,
	completeRefunds,
	convertFree,
	createBankBin,
	createCoupon,
	createDate,
	createDateQuestion,
	createDelivery,
	createDeposit,
	createEvent,
	createEventCodes,
	createEventGroup,
	createLocation,
	createOrder,
	createPaymentTerminal,
	createPayout,
	createPayoutGroup,
	createPDV,
	createPDVCorte,
	createPdvDeposit,
	createPromoter,
	createPromoterBankAccount,
	createPromoterContact,
	createQuestionValue,
	createRefund,
	createRefundableRequest,
	createScanner,
	createSectionHold,
	createTicketsPayment,
	createUser,
	createVenue,
	createVenueSection,
	createZone,
	deleteAsset,
	deleteBank,
	deleteBinNumber,
	deleteCoupon,
	deleteDeposit,
	deleteEventDate,
	deleteEventPaymentPlan,
	deletePaymentTerminal,
	deletePdvDeposit,
	deletePDVEvent,
	deletePDVZone,
	deletePromoterContact,
	deleteQuestion,
	deleteQuestionValue,
	deleteSection,
	deleteSectionHold,
	deleteTicketUse,
	deleteVenue,
	doConcilia,
	downloadEventCodes,
	downloadLink,
	downloadPapeletaZip,
	downloadPayoutPoliza,
	downloadPayoutSTP,
	downloadPayoutZip,
	downloadPdvDeposits,
	downloadRefundZip,
	duplicateDate,
	editBankBin,
	editCoupon,
	editDate,
	editDatePdvMethods,
	editDateRefundOptions,
	editDelivery,
	editEvent,
	editEventGroup,
	editLicenseTerminal,
	editOrder,
	editPaymentTerminal,
	editPDV,
	editPdvPaymentMethods,
	editPrice,
	editPromoter,
	editPromoterContact,
	editScanner,
	editTerminalSerial,
	editTicket,
	editTicketForm,
	editUser,
	editVenue,
	editVenueSection,
	editZone,
	executeRefund,
	expireOrder,
	extendOrder,
	getAcquirerService,
	getAllReportsAccess,
	getAllVenues,
	getAllVenueSections,
	getAmexAccounting,
	getAuthUrl,
	getBank,
	getBankBinList,
	getBusinessBanks,
	getCityBuyers,
	getCityVenues,
	getConcilia,
	getCorteDates,
	getCoupon,
	getCouponList,
	getDate,
	getDateBalanceReport,
	getDateDelivery,
	getDateDepositSources,
	getDatePayments,
	getDatePaymentsList,
	getDateRefundableRequests,
	getDateSeatDump,
	getDateSeatmap,
	getDateSectionHolders,
	getDateSections,
	getDateSectionStatus,
	getDeliveryMethod,
	getDeliveryMethods,
	getEvent,
	getEventBalance,
	getEventBusinessSources,
	getEventCodeGroup,
	getEventCodes,
	getEventDateForms,
	getEventDateQuestion,
	getEventDateQuestions,
	getEventDates,
	getEventDepositSources,
	getEventGroup,
	getEventGroups,
	getEventListReport,
	getEventOrders,
	getEventOrdersConcilia,
	getEventPaymentPlans,
	getEventPdvSales,
	getEventSalesReport,
	getEventSectionPrices,
	getEventSections,
	getEventsQL,
	getEventStatus,
	getIncomeReport,
	getInvoices,
	getLicenses,
	getLocationInfo,
	getLogin,
	getOrder,
	getOrderCreatorDate,
	getOrderEmails,
	getOrderInvoices,
	getOrderPdf,
	getPapeletaDay,
	getPapeletaDays,
	getPapeletaPdv,
	getPaymentConcilia,
	getPaymentInfo,
	getPaymentPlans,
	getPaymentTerminal,
	getPaymentTerminalList,
	getPayout,
	getPayoutAssets,
	getPayoutGroup,
	getPayouts,
	getPayoutsReport,
	getPDV,
	getPDVAcces,
	getPDVAccesUser,
	getPdvConciliaCortes,
	getPdvConciliaDeposits,
	getPdvConciliaList,
	getPdvCorteDeposits,
	getPdvCorteDetail,
	getPDVCorteInfo,
	getPdvCorteLog,
	getPDVCortes,
	getPdvCortesReport,
	getPdvDeposit,
	getPdvDepositSource,
	getPdvDepositTypes,
	getPDVEvents,
	getPDVList,
	getPdvOrderConcilia,
	getPdvPaymentMethods,
	getPDVSalesLog,
	getPdvSalesReport,
	getPdvStats,
	getPdvTicketLog,
	getPDVZones,
	getPopularDeliveryMethods,
	getPopularEvents,
	getPopularZones,
	getPromoter,
	getPromoterBalance,
	getPromoterBankAccount,
	getPromoterBankAccounts,
	getPromoterContact,
	getPromoterContacts,
	getPromoterEvents,
	getPromoterEventsBalance,
	getPromoters,
	getRecentOrders,
	getRefund,
	getRefunds,
	getResetPasswordLink,
	getScanner,
	getScannerList,
	getSystemConfig,
	getSystemStats,
	getTemplateTest,
	getTicketForm,
	getTicketPrints,
	getTicketTemplate,
	getTicketTemplates,
	getTicketUses,
	getToken,
	getUser,
	getUserFromEmail,
	getUserList,
	getUserLogins,
	getUserReportsAccess,
	getUsers,
	getVenue,
	getVenueSection,
	getZone,
	getZoneEventsList,
	getZonesList,
	isSeatDeletable,
	listActiveQueues,
	listEvents,
	logout,
	moveTickets,
	onLoginToken,
	pendingRefunds,
	refreshUserEvents,
	refreshUserSecret,
	reinitializePdv,
	reinitializeScanner,
	reinitializeTerminal,
	rejectPayoutGroup,
	rejectPayouts,
	removeDateDelivery,
	removeDateSection,
	removedRefundEvidence,
	removeEventMap,
	removeEventPoster,
	removePaymentMethod,
	removeUserReportsEvent,
	removeUserReportsPromoter,
	resendEmail,
	resendOrderEmail,
	resendRefundRequest,
	resetPayouts,
	resetScannerToken,
	revertRefundPayments,
	reviveTickets,
	saveAccess,
	saveBinNumbers: addBinNumbers,
	saveDepositPayment,
	saveEventPaymentPlan,
	savePDVEvent,
	savePDVZone,
	saveSeats,
	saveSinopsis,
	searchEvent,
	searchOrders,
	searchPdv,
	searchPromoter,
	searchZone,
	sendRefundInfoRequest,
	setDateQueue,
	setDateSeatCost,
	setDateSeatStatus,
	setDateSectionTicketCount,
	setEventBank,
	setEventGroupPoster,
	setEventOptions,
	setEventPoster,
	setEventSecret,
	setEventUserAssigned,
	setPaymentPaid,
	setPromoterUserAssigned,
	setQueueConcurrent,
	setQueuePaused,
	setSystemConfig,
	setTicketHidden,
	ticketUse,
	updateDateQuestion,
	updateDateSectionsOrder,
	updateLocation,
	updateOrderLocation,
	updatePayment,
	updatePriceOrder,
	updatePromoterBankAccount,
	updateQuestionOrder,
	updateQuestionValue,
	updateQuestionValueOrder,
	updateSectionHold,
	updateTicketSeat,
	updateUserSystemAccess,
	uploadAsset,
	uploadEventMap,
	uploadRefundEvidence,
	verifyPdvDeposit,
	viewRefundRequest,
}